import { lazy, Suspense, useEffect, useState } from "react"
import { Navigate, Route, Routes, useHref } from "react-router-dom"

import { HeaderMenu } from "entities/headerMenu/index"
import { SubscriptionBanner } from "shared/ui/subscriptionBanner/SubscriptionBanner"
import { AuthSkeleton } from "widgets/fallbacks/AuthSkeleton"
import { CalendarSkeleton } from "widgets/fallbacks/CalendarSkeleton"
import { PageSkeleton } from "widgets/fallbacks/PageSkeleton"
import { TableSkeleton } from "widgets/fallbacks/TableSkeleton"
import { Navbar } from "widgets/navbar/index"
import { MobileNav } from "widgets/navbar/MobileNav"
import { MobileSidebar } from "widgets/navbar/MobileSidebar"
import { getPaths } from "./routes"

// non-lazy pages
// import SalonCalendar from "pages/bookingsCalendar"

import { AuthPage } from "pages/auth"
import UserProfile from "pages/profile/UserProfile"

import { useStore } from "hooks"
import { observer } from "mobx-react-lite"
import ChangePass from "pages/auth/changePass/index"
import SignIn from "pages/auth/signIn/index"
import { Feedback } from "pages/feedback"
// import Bookings from "../../pages/bookings"
import ClientConfirmation from "pages/clientConfirmation/clientConfirmation"
import RestaurantSms from "pages/restaurarantSmS"
import SalonsOwner from "pages/salonsOwner/SalonsOwner"
import { useDispatch, useSelector } from "react-redux"
import { fetchStatuses } from "../../pages/bookings/statusesSlice"
import { Analyzes } from "../../pages/laboratory/analyzes/Analyzes"
import { FormPage } from "../../pages/laboratory/analyzes/formPage/FormPage"
import { Templates } from "../../pages/laboratory/templates/Templates"
import Transactions from "../../pages/transaction"
import { getMeData } from "./slice"
import { setStep } from "./stepSlice"

const publicLazyRoutes = [
  { path: "/signIn", Component: SignIn, Fallback: AuthSkeleton },
  { path: "/changePass", Component: ChangePass, Fallback: AuthSkeleton },
]

const AdminBookingList = lazy(() => import("pages/bookings"))
const AdminBookingCalendarList = lazy(
  () => import("pages/bookingsCalendar/index")
)
const Masters = lazy(() => import("pages/masters/index"))
const Settings = lazy(() => import("pages/settings/index"))

const Analytics = lazy(() => import("pages/analytics/index"))
const Operations = lazy(() => import("pages/finance/operations/index"))
const Salary = lazy(() => import("pages/finance/salary/index"))
const Counterparties = lazy(() => import("pages/directories/counterparties/index"))
const Items = lazy(() => import("pages/directories/items/index"))
const Accounts = lazy(() => import("pages/directories/accounts/index"))

const adminLazyPathways = [
  { path: "/bookings", Component: AdminBookingList, Fallback: TableSkeleton },
  {
    path: "/bookings-calendar",
    Component: AdminBookingCalendarList,
    Fallback: CalendarSkeleton,
  },
  { path: "/masters", Component: Masters, Fallback: () => null },
  { path: "/settings", Component: Settings, Fallback: PageSkeleton },
  { path: "/laboratory", Component: Masters, Fallback: () => null },
  { path: "/feedback", Component: Feedback, Fallback: PageSkeleton },
  { path: "/profile", Component: UserProfile, Fallback: PageSkeleton },
  { path: "/transactions", Component: Transactions, Fallback: TableSkeleton },
  { path: "/analytics", Component: Analytics, Fallback: TableSkeleton },
  { path: "/finance/operations", Component: Operations, Fallback: TableSkeleton },
  { path: "/finance/salary", Component: Salary, Fallback: TableSkeleton },
  { path: "/directories/counterparties", Component: Counterparties, Fallback: TableSkeleton },
  { path: "/directories/items", Component: Items, Fallback: TableSkeleton },
  { path: "/directories/accounts", Component: Accounts, Fallback: TableSkeleton },
]

const Customers = lazy(() => import("pages/customers/index"))
const categories = lazy(() => import("pages/categories/index"))
const Services = lazy(() => import("pages/services/index"))
const Reviews = lazy(() => import("pages/reviews/index"))
const Finance = lazy(() => import("pages/finance/index"))
const Subscription = lazy(() => import("pages/subscription/index"))

const noPermissionPathways = [
  { path: "/customers", Component: Customers, Fallback: TableSkeleton },
  { path: "/laboratory", Component: Masters, Fallback: () => null },
  { path: "/categories", Component: categories, Fallback: TableSkeleton },
  { path: "/services", Component: Services, Fallback: TableSkeleton },
  { path: "/reviews", Component: Reviews, Fallback: TableSkeleton },
  { path: "/finance", Component: Finance, Fallback: TableSkeleton },
  { path: "/subscription", Component: Subscription, Fallback: PageSkeleton },
  { path: "/analytics", Component: Analytics, Fallback: TableSkeleton },
  { path: "/finance/operations", Component: Operations, Fallback: TableSkeleton },
  { path: "/finance/salary", Component: Salary, Fallback: TableSkeleton },
  { path: "/directories/counterparties", Component: Counterparties, Fallback: TableSkeleton },
  { path: "/directories/items", Component: Items, Fallback: TableSkeleton },
  { path: "/directories/accounts", Component: Accounts, Fallback: TableSkeleton },
]
const pathList = getPaths(true)

const user = JSON.parse(localStorage.getItem("user"))
const localStep = user?.step

export const Homepage = observer(() => {
  const dispatch = useDispatch()
  const path = useHref()
  const { step } = useSelector((state) => state.StepSliceReducer)
  const { auth } = useStore()

  // Fetch user data and statuses on component mount to ensure Redux store is up to date
  // Only fetch if token exists to avoid 401 errors
  const { statuses, loading: statusesLoading } = useSelector(
    (state) => state.statusesSliceReducer
  )

  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken")
    if (accessToken) {
      dispatch(getMeData())
      // Загружаем статусы если они еще не загружены
      if (!statuses || statuses.length === 0) {
        dispatch(fetchStatuses())
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  // Initialize step when component mounts or when auth user changes
  useEffect(() => {
    // Get the most up-to-date step value
    const currentStep = auth.user?.step

    // If we have a valid step from the auth store, use it
    if (currentStep && ["0", "1", "2", "3"].includes(currentStep)) {
      dispatch(setStep(currentStep))
    } else {
      // Fallback to localStorage
      const user = JSON.parse(localStorage.getItem("user"))
      const localStep = user?.step
      if (localStep && ["0", "1", "2", "3"].includes(localStep)) {
        dispatch(setStep(localStep))
      }
      // Note: We don't set a default value here anymore as the initial state is now ""
      // The UI components should handle the empty step case appropriately
    }
  }, [dispatch, auth.user?.step]) // Run when auth.user.step changes

  const [hiddenNavbar, setHiddenNavbar] = useState(false)
  const [hiddenMobileNavbar, setHiddenMobileNavbar] = useState(true)
  const [showNavigation, setShowNavigation] = useState(true)

  const isAdmin = true
  const isAuthorized = auth.accessToken.length > 0

  function toggleMobileNavbar() {
    setHiddenMobileNavbar(!hiddenMobileNavbar)
  }

  useEffect(() => {
    if (
      path.startsWith("/b/") ||
      path.startsWith("/s/") ||
      path.startsWith("/r/")
    ) {
      setShowNavigation(false)
    }
  }, [path])

  return (
    <>
      {isAuthorized ? (
        <div className={`flex min-h-[100vh] relative`}>
          {showNavigation && (
            <>
              <Navbar
                hidden={hiddenNavbar}
                setHidden={setHiddenNavbar}
                pathList={pathList}
              />
              <MobileNav pathList={pathList} />
              <MobileSidebar
                isHidden={hiddenMobileNavbar}
                pathList={pathList}
                toggleNavbar={toggleMobileNavbar}
              />
            </>
          )}
          <div
            className={`sm:pb-[25px] w-full flex flex-col sm:px-[40px] ${showNavigation ? "" : ""} sm:pt-6 h-screen overflow-auto`}
          >
            {showNavigation && <HeaderMenu toggleNavbar={toggleMobileNavbar} />}
            {showNavigation && <SubscriptionBanner />}

            <Routes>
              {/* pathways available without permission */}
              {noPermissionPathways.map(({ path, Component, Fallback }) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <Suspense fallback={<Fallback />}>
                      <Component />
                    </Suspense>
                  }
                />
              ))}

              {isAdmin ? (
                <>
                  <Route path='/b/:hash' element={<ClientConfirmation />} />
                  <Route path='/s/:hash' element={<SalonsOwner />} />
                  <Route path='/r/:hash' element={<RestaurantSms />} />

                  {/*<Route path="/bookings" element={<Bookings />} />*/}
                  {adminLazyPathways.map(({ path, Component, Fallback }) => (
                    <Route
                      key={path}
                      path={path}
                      element={
                        <Suspense fallback={<Fallback />}>
                          <Component />
                        </Suspense>
                      }
                    />
                  ))}
                  <Route
                    path='*'
                    element={<Navigate to='/bookings-calendar' />}
                  />

                  <Route path='/templates' element={<Templates />} />
                  <Route path='/analyzes' element={<Analyzes />} />
                  <Route path='/analyzes/new' element={<FormPage />} />
                  <Route path='/analyzes/:id' element={<FormPage />} />
                </>
              ) : (
                <>
                  {/* <Route path='/bookings-calendar' element={<PersonalCalendar />} />
                  <Route path='/schedules' element={<PersonalWorkTimeCalendar />} />
                  {userLazyPathways.map(({ path, Component, Fallback }) => (
                    <Route
                      key={path}
                      path={path}
                      element={
                        <Suspense fallback={<Fallback />}>
                          <Component />
                        </Suspense>
                      }
                    />
                  ))} */}
                </>
              )}
            </Routes>
          </div>
        </div>
      ) : (
        // public pathways
        <Routes>
          {/*<Route path='/settings' element={<Settings/>}/>*/}

          <Route path='/b/:hash' element={<ClientConfirmation />} />
          <Route path='/s/:hash' element={<SalonsOwner />} />
          <Route path='/r/:hash' element={<RestaurantSms />} />

          <Route index path='/login' element={<AuthPage />} />
          <Route path='*' element={<Navigate to='/login' />} />
          {publicLazyRoutes.map(({ path, Component, Fallback }) => (
            <Route
              key={path}
              path={path}
              element={
                <Suspense fallback={<Fallback />}>
                  <Component />
                </Suspense>
              }
            />
          ))}
        </Routes>
      )}
    </>
  )
})